import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import {
  ApiKeyListDto,
  BillingPeriodsResponse,
  CreateKeyInput,
  CreateKeyResponse,
  DashboardSummaryDto,
  KeyStatus,
  KnownScopeDto,
  PlanInput,
  PricingPlanDto,
  UsageResponse,
  useExternalApiClient,
} from "../clients/ExternalApiClient";

export interface ApiKeyRepository {
  useKeys(): Resource<ApiKeyListDto[]>;
  useKnownScopes(): Resource<KnownScopeDto[]>;
  usePlan(id: number): Resource<PricingPlanDto | null>;
  useUsage(
    id: number,
    from: string,
    to: string,
    groupBy: "day" | "hour" | "scope",
  ): Resource<UsageResponse>;
  useBillingPeriods(id: number, limit?: number): Resource<BillingPeriodsResponse>;
  useDashboardSummary(): Resource<DashboardSummaryDto>;
  createKey: (input: CreateKeyInput) => Promise<CreateKeyResponse>;
  updateKeyStatus: (id: number, status: KeyStatus) => Promise<void>;
  updateKeyScopes: (id: number, scopes: string[]) => Promise<void>;
  upsertPlan: (id: number, input: PlanInput) => Promise<PricingPlanDto>;
}

export function useApiKeyRepository(): ApiKeyRepository {
  const client = useExternalApiClient();
  const queryClient = useQueryClient();
  const config = { staleTime: 30_000 };

  function useKeys(): Resource<ApiKeyListDto[]> {
    return useQuery(["apiKeys"], () => client.listKeys(), config);
  }

  function useKnownScopes(): Resource<KnownScopeDto[]> {
    return useQuery(["apiKeys", "knownScopes"], () => client.listScopes(), {
      staleTime: 5 * 60_000,
    });
  }

  function usePlan(id: number): Resource<PricingPlanDto | null> {
    return useQuery(
      ["apiKeys", id, "plan"],
      () => client.getPlan(id),
      { ...config, enabled: id > 0 },
    );
  }

  function useUsage(
    id: number,
    from: string,
    to: string,
    groupBy: "day" | "hour" | "scope",
  ): Resource<UsageResponse> {
    return useQuery(
      ["apiKeys", id, "usage", from, to, groupBy],
      () => client.getUsage(id, from, to, groupBy),
      { ...config, enabled: id > 0 },
    );
  }

  function useBillingPeriods(
    id: number,
    limit = 12,
  ): Resource<BillingPeriodsResponse> {
    return useQuery(
      ["apiKeys", id, "billing-periods", limit],
      () => client.getBillingPeriods(id, limit),
      { ...config, enabled: id > 0 },
    );
  }

  function useDashboardSummary(): Resource<DashboardSummaryDto> {
    return useQuery(
      ["apiKeys", "dashboard"],
      () => client.getDashboardSummary(),
      config,
    );
  }

  const createKey = useMutation(
    (input: CreateKeyInput) => client.createKey(input),
    {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["apiKeys"] }),
    },
  );

  const updateKeyStatus = useMutation(
    ({ id, status }: { id: number; status: KeyStatus }) =>
      client.updateKeyStatus(id, status),
    {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["apiKeys"] }),
    },
  );

  const updateKeyScopes = useMutation(
    ({ id, scopes }: { id: number; scopes: string[] }) =>
      client.updateKeyScopes(id, scopes),
    {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["apiKeys"] }),
    },
  );

  const upsertPlan = useMutation(
    ({ id, input }: { id: number; input: PlanInput }) =>
      client.upsertPlan(id, input),
    {
      onSuccess: (_data, vars) => {
        queryClient.invalidateQueries({ queryKey: ["apiKeys"] });
        queryClient.invalidateQueries({ queryKey: ["apiKeys", vars.id] });
      },
    },
  );

  return {
    useKeys,
    useKnownScopes,
    usePlan,
    useUsage,
    useBillingPeriods,
    useDashboardSummary,
    createKey: (input) => createKey.mutateAsync(input),
    updateKeyStatus: (id, status) =>
      updateKeyStatus.mutateAsync({ id, status }).then(() => undefined),
    updateKeyScopes: (id, scopes) =>
      updateKeyScopes.mutateAsync({ id, scopes }).then(() => undefined),
    upsertPlan: (id, input) => upsertPlan.mutateAsync({ id, input }),
  };
}
